import { zodResolver } from "@hookform/resolvers/zod";
import { createContext, ReactNode, useState } from "react";
import {
  FormProvider,
  useForm,
  useFormContext,
  UseFormProps,
} from "react-hook-form";
import {
  PipelineConfigForm,
  PipelineConfigFormSchema,
} from "../../../schemas/OrionConfig";
import { useNonNullableContext } from "../../utils/nonNullableContext";

interface ConfigModeContextType {
  isNew: boolean;
  isVisMode: boolean;
  isReadOnly: boolean;
  setIsVisMode: React.Dispatch<React.SetStateAction<boolean>>;
}
type ConfigFormProps = Omit<UseFormProps<PipelineConfigForm>, "resolver">;

const ConfigModeContext = createContext<ConfigModeContextType | undefined>(
  undefined
);

interface ConfigFormProviderProps {
  children: ReactNode;
  isNew: boolean;
  isReadOnly: boolean;
  formProps: ConfigFormProps;
}

export const ConfigFormProvider = ({
  children,
  isNew,
  isReadOnly,
  formProps,
}: ConfigFormProviderProps) => {
  const [isVisMode, setIsVisMode] = useState(false);

  const methods = useForm<PipelineConfigForm>({
    ...formProps,
    disabled: isReadOnly,
    resolver: zodResolver(PipelineConfigFormSchema),
  });

  const modeValue = {
    isNew,
    isVisMode,
    isReadOnly,
    setIsVisMode,
  };

  return (
    <ConfigModeContext.Provider value={modeValue}>
      <FormProvider {...methods}>{children}</FormProvider>
    </ConfigModeContext.Provider>
  );
};

export const useConfigModeContext = () => {
  return useNonNullableContext(ConfigModeContext, "ConfigModeContext");
};

export const useFormConfigContext = () => useFormContext<PipelineConfigForm>();
